import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { SubscriptionStatus, UploadLimits, Features, paymentService } from '../../services/paymentService';
import { toastHelper } from '../../utils/toastHelper.js';
import { usePermissions } from '../../contexts/PermissionContext';
import { PermissionDisplayMetadata } from '../../services/permissionService';
import { TIER_LABELS, SubscriptionLimit } from './types';
import { TIER_AUDIO_LIMITS } from '../../config/audioLimitsConfig';

interface SubscriptionLimitsProps {
  subscriptionStatus: SubscriptionStatus | null;
  uploadLimits: UploadLimits | null;
  features: Features | null;
  tierLimits: Map<string, SubscriptionLimit>;
  countdown: number;
}

const SubscriptionLimits: React.FC<SubscriptionLimitsProps> = ({
  subscriptionStatus,
  uploadLimits,
  features,
  tierLimits,
  countdown,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const expandedContentRef = useRef<HTMLDivElement>(null);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const { getSubscriptionFeatures, permissionsMetadata, isLoading: permissionsLoading } = usePermissions();

  const tier = subscriptionStatus?.subscription_tier || 'free';
  const tierLabel = TIER_LABELS[tier] || tier;
  
  // Get subscription features to display (sorted by display_order)
  const subscriptionFeatures = getSubscriptionFeatures();
  
  // Fallback feature list if metadata isn't loaded yet
  const fallbackFeatures: PermissionDisplayMetadata[] = [
    { name: 'audio_limits', display_name: 'Audio Upload Limits', display_description: '', icon_name: 'music', display_order: 1, category: 'upload', show_in_subscription_table: true, show_in_user_dashboard: true, help_text: '' },
    { name: 'instant_uploads', display_name: 'Instant Uploads', display_description: '', icon_name: 'upload', display_order: 2, category: 'upload', show_in_subscription_table: true, show_in_user_dashboard: true, help_text: '' },
    { name: 'auto_resize_image', display_name: 'Auto Resize Image', display_description: '', icon_name: 'image', display_order: 3, category: 'image_processing', show_in_subscription_table: true, show_in_user_dashboard: true, help_text: '' },
    { name: 'scheduled_uploads', display_name: 'Scheduled Uploads', display_description: '', icon_name: 'calendar', display_order: 4, category: 'upload', show_in_subscription_table: true, show_in_user_dashboard: true, help_text: '' },
    { name: 'crop_image', display_name: 'Crop Image', display_description: '', icon_name: 'crop', display_order: 5, category: 'image_processing', show_in_subscription_table: true, show_in_user_dashboard: true, help_text: '' },
    { name: 'upload_defaults', display_name: 'Upload Defaults', display_description: '', icon_name: 'settings', display_order: 6, category: 'upload', show_in_subscription_table: true, show_in_user_dashboard: true, help_text: '' },
  ];
  
  // Use metadata if available, otherwise use fallback
  let featuresToDisplay = subscriptionFeatures.length > 0 ? subscriptionFeatures : fallbackFeatures;

  // Always ensure audio_limits is included (it's not in the database, it's config-based)
  const hasAudioLimits = featuresToDisplay.some(f => f.name === 'audio_limits');
  if (!hasAudioLimits) {
    // Insert audio_limits at the beginning (first position)
    const audioLimitsFeature: PermissionDisplayMetadata = {
      name: 'audio_limits',
      display_name: 'Audio Upload Limits',
      display_description: 'Maximum audio file size and supported formats',
      icon_name: 'music',
      display_order: 1,
      category: 'upload',
      show_in_subscription_table: true,
      show_in_user_dashboard: true,
      help_text: 'Audio file size and format limits vary by tier'
    };

    // Always add at the beginning
    featuresToDisplay = [audioLimitsFeature, ...featuresToDisplay];
  }
  
  // Debug: Log if features are empty
  useEffect(() => {
    if (!permissionsLoading && subscriptionFeatures.length === 0 && permissionsMetadata.length === 0) {
      console.warn('No subscription features found. Using fallback. Metadata:', permissionsMetadata);
    }
  }, [subscriptionFeatures, permissionsMetadata, permissionsLoading]);
  
  // Helper function to render feature cell for a tier
  const renderFeatureCell = (tierName: string, featureName: string) => {
    const limits = tierLimits.get(tierName);
    if (!limits) return null;

    // Special handling for number_of_accounts (show the count value)
    if (featureName === 'number_of_accounts') {
      // For number_of_accounts, we want to show the actual limit_value from role_permissions
      // This is fetched from the backend as part of subscription limits
      // Family = 4, all others = 1
      const accountCount = tierName === 'family' ? 4 : 1;
      return (
        <Badge variant="outline" className="text-sm">
          {accountCount}
        </Badge>
      );
    }

    // Special handling for instant_uploads (uses daily_instant_upload_limit)
    if (featureName === 'instant_uploads') {
      const limit = limits.daily_instant_upload_limit;
      return limit === null ? (
        <Badge variant="default" className="bg-green-500 text-xs">
          Unlimited
        </Badge>
      ) : (
        <Badge variant="outline" className="text-xs">{limit} per day</Badge>
      );
    }

    // Special handling for audio_limits
    if (featureName === 'audio_limits') {
      const audioLimits = TIER_AUDIO_LIMITS[tierName] || TIER_AUDIO_LIMITS.free;
      return (
        <div className="text-xs">
          <Badge variant="outline" className="text-xs">{audioLimits.maxFileSizeMb}MB</Badge>
          <div className="text-xs text-muted-foreground mt-1 break-words">
            {audioLimits.displayFormats}
          </div>
        </div>
      );
    }

    // Special handling for scheduled_uploads (uses allow_scheduled_uploads and daily_scheduled_upload_limit)
    if (featureName === 'scheduled_uploads') {
      if (!limits.allow_scheduled_uploads) {
        return (
          <Badge variant="destructive" className="text-xs">
            Not Available
          </Badge>
        );
      }
      const limit = limits.daily_scheduled_upload_limit;
      return (
        <Badge variant="default" className="bg-green-500 text-xs">
          {limit === null
            ? 'Unlimited'
            : limit === 0
            ? 'Not Available'
            : `${limit} per day`}
        </Badge>
      );
    }

    // Special handling for upload_defaults (uses allow_upload_defaults)
    if (featureName === 'upload_defaults') {
      const hasFeature = limits.allow_upload_defaults;
      return hasFeature ? (
        <Badge variant="default" className="bg-green-500 text-xs">
          Available
        </Badge>
      ) : (
        <Badge variant="destructive" className="text-xs">
          Not Available
        </Badge>
      );
    }

    // For other features, check the features object (now dynamic)
    const hasFeature = limits.features?.[featureName] === true;
    return hasFeature ? (
      <Badge variant="default" className="bg-green-500 text-xs">
        Available
      </Badge>
    ) : (
      <Badge variant="destructive" className="text-xs">
        Not Available
      </Badge>
    );
  };
  
  // Helper to render feature name with tooltip (desktop view only)
  const renderFeatureName = (featureMeta: PermissionDisplayMetadata) => {
    if (!featureMeta) return null;

    // For scheduled_uploads, append "(up to 30 days in advance)" on desktop view
    const displayName = featureMeta.name === 'scheduled_uploads'
      ? `${featureMeta.display_name} (up to 30 days in advance)`
      : featureMeta.display_name;

    return (
      <div className="flex items-center gap-2">
        <span className="font-medium">{displayName}</span>
        {featureMeta.help_text && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-muted-foreground cursor-help">?</span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">{featureMeta.help_text}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  };

  // Auto-scroll to expanded content when expanded, blur when collapsed
  useEffect(() => {
    if (isExpanded && expandedContentRef.current) {
      // Delay to ensure the transition has started and content is rendering
      const timer = setTimeout(() => {
        if (expandedContentRef.current) {
          // Custom smooth scroll that's slower and ensures full visibility in one scroll
          // Don't call focus() here as it causes an instant scroll - we only want our smooth scroll
          const element = expandedContentRef.current;
          const elementRect = element.getBoundingClientRect();
          const elementTop = elementRect.top + window.pageYOffset;
          const elementHeight = elementRect.height;
          const viewportHeight = window.innerHeight;
          const elementBottom = elementTop + elementHeight;
          
          // Calculate the final scroll position to show the entire element
          // We want the bottom of the element to be visible with some padding
          const bottomPadding = 40; // Padding from bottom of viewport
          const topOffset = 100; // Offset from top of viewport
          
          // Calculate scroll position that shows the full element
          // If element is taller than viewport, scroll to show the bottom
          // Otherwise, scroll to show the top with offset
          let targetScroll: number;
          if (elementHeight > viewportHeight - topOffset - bottomPadding) {
            // Element is tall, scroll to show the bottom
            targetScroll = elementBottom - viewportHeight + bottomPadding;
          } else {
            // Element fits, scroll to show the top with offset
            targetScroll = elementTop - topOffset;
          }
          
          // Ensure we don't scroll to negative values
          targetScroll = Math.max(0, targetScroll);
          
          // Get current scroll position
          const startScroll = window.pageYOffset;
          const distance = targetScroll - startScroll;
          const duration = 800; // Slower scroll (800ms instead of default ~300ms)
          let startTime: number | null = null;
          
          // Easing function for smooth animation
          const easeInOutCubic = (t: number): number => {
            return t < 0.5
              ? 4 * t * t * t
              : 1 - Math.pow(-2 * t + 2, 3) / 2;
          };
          
          const animateScroll = (currentTime: number) => {
            if (startTime === null) startTime = currentTime;
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const easedProgress = easeInOutCubic(progress);
            
            window.scrollTo({
              top: startScroll + distance * easedProgress,
              behavior: 'auto' // Use 'auto' since we're manually animating
            });
            
            if (progress < 1) {
              requestAnimationFrame(animateScroll);
            } else {
              // Scroll complete - optionally focus for accessibility (doesn't cause scroll since we're already there)
              expandedContentRef.current?.focus();
            }
          };
          
          requestAnimationFrame(animateScroll);
        }
      }, 200); // Slightly longer delay to ensure content is fully rendered
      return () => clearTimeout(timer);
    } else if (!isExpanded && expandedContentRef.current) {
      expandedContentRef.current.blur();
    }
  }, [isExpanded]);

  return (
    <Card className="mb-6 w-full max-w-full">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              Subscription & Limits
            </CardTitle>
            <CardDescription className="break-words">Your current subscription tier, status, and upload limits</CardDescription>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={cn(
              "flex items-center gap-2 shrink-0 self-start sm:self-auto",
              "text-base font-semibold py-3 px-4 border rounded-md transition-colors cursor-pointer",
              "border-primary/50 hover:border-primary hover:bg-primary/10",
              "text-foreground"
            )}
          >
            {isExpanded ? (
              <>
                <span className="hidden sm:inline">Hide Details</span>
                <span className="sm:hidden">Hide</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Show Details</span>
                <span className="sm:hidden">Show</span>
              </>
            )}
          </button>
        </div>
      </CardHeader>
      <CardContent className="w-full max-w-full overflow-x-hidden">
        {/* Summary View (Always Visible) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-muted-foreground mb-1">Tier</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={tier === 'free' ? 'secondary' : 'default'} className="text-base">
                {tierLabel}
              </Badge>
              {subscriptionStatus?.is_complimentary && (
                <Badge variant="outline" className="text-xs">
                  Complimentary
                </Badge>
              )}
            </div>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-muted-foreground mb-1">Status</p>
            <Badge 
              variant={subscriptionStatus?.subscription_status === 'active' ? 'default' : 'secondary'}
              className="break-words"
            >
              {subscriptionStatus?.subscription_status || 'active'}
            </Badge>
          </div>
          {uploadLimits && (
            <div className="min-w-0">
              <p className="text-sm font-medium text-muted-foreground mb-1">Instant Uploads</p>
              <p className="text-xl font-bold break-words">
                {uploadLimits.instant_count}
                {uploadLimits.instant_limit !== null && uploadLimits.instant_limit !== undefined 
                  ? ` / ${uploadLimits.instant_limit}` 
                  : ' / ∞'}
              </p>
            </div>
          )}
          {uploadLimits && (
            <div className="min-w-0">
              <p className="text-sm font-medium text-muted-foreground mb-1">Scheduled Uploads</p>
              <p className="text-xl font-bold break-words">
                {uploadLimits.scheduled_count}
                {uploadLimits.allow_scheduled_uploads 
                  ? (uploadLimits.scheduled_limit !== null && uploadLimits.scheduled_limit !== undefined 
                      ? ` / ${uploadLimits.scheduled_limit}` 
                      : ' / ∞')
                  : ' / 0'}
              </p>
            </div>
          )}
        </div>

        {/* Expanded Details View */}
        <div 
          ref={expandedContentRef}
          tabIndex={-1}
          className={cn(
            "overflow-hidden transition-all duration-300 ease-in-out outline-none",
            isExpanded ? "max-h-[2000px] opacity-100 mt-6" : "max-h-0 opacity-0 mt-0"
          )}
          aria-label={isExpanded ? "Subscription details" : undefined}
        >
          <div className="space-y-6 w-full max-w-full overflow-x-hidden">
            <Separator />
            
            {/* Subscription Details */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Subscription Details</h3>
                <div className="flex items-center gap-4 text-sm">
                  {subscriptionStatus?.next_billing_date && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-muted-foreground">Next billing date:</span>
                      <span>{new Date(subscriptionStatus.next_billing_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {subscriptionStatus?.next_billing_date && subscriptionStatus?.subscription_start_date && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-muted-foreground">Subscription active until:</span>
                      <span>{new Date(subscriptionStatus.next_billing_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
                {subscriptionStatus?.complimentary_reason && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Reason</p>
                    <p className="text-sm">{subscriptionStatus.complimentary_reason}</p>
                  </div>
                )}
            </div>


            {/* Features Comparison Table - All Tiers */}
            <div className="w-full max-w-full">
              <h3 className="text-lg font-semibold mb-3">Features by Plan</h3>
              
              {/* Desktop View - Table Format */}
              <div className="hidden sm:block border rounded-lg overflow-x-auto w-full">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[35%] px-3 py-2">Feature</TableHead>
                      <TableHead className="text-center w-[13%] px-2 py-2 text-xs">Free</TableHead>
                      <TableHead className="text-center w-[13%] px-2 py-2 text-xs">Starter</TableHead>
                      <TableHead className="text-center w-[13%] px-2 py-2 text-xs">Pro</TableHead>
                      <TableHead className="text-center w-[13%] px-2 py-2 text-xs">Family</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {featuresToDisplay.length > 0 ? (
                      featuresToDisplay.map((featureMeta) => (
                        <TableRow key={featureMeta.name}>
                          <TableCell className="px-3 py-2 text-sm">
                            {renderFeatureName(featureMeta)}
                          </TableCell>
                          <TableCell className="text-center px-2 py-2">
                            {renderFeatureCell('free', featureMeta.name)}
                          </TableCell>
                          <TableCell className="text-center px-2 py-2">
                            {renderFeatureCell('starter', featureMeta.name)}
                          </TableCell>
                          <TableCell className="text-center px-2 py-2">
                            {renderFeatureCell('pro', featureMeta.name)}
                          </TableCell>
                          <TableCell className="text-center px-2 py-2">
                            {renderFeatureCell('family', featureMeta.name)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground px-3 py-2">
                          {permissionsLoading ? 'Loading features...' : 'No features available'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile View - List Format */}
              <div className="block sm:hidden space-y-3">
                {['free', 'starter', 'pro', 'family'].map(tier => {
                  const limits = tierLimits.get(tier);
                  if (!limits) return null;
                  return (
                    <Card key={tier} className="p-3">
                      <h4 className="font-semibold mb-2 capitalize">{TIER_LABELS[tier] || tier}</h4>
                      <div className="space-y-2 text-sm">
                        {featuresToDisplay.length > 0 ? (
                          featuresToDisplay.map((featureMeta) => {
                            return (
                              <div key={featureMeta.name} className="flex items-center justify-between">
                                <span>{featureMeta.display_name}</span>
                                {renderFeatureCell(tier, featureMeta.name)}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center text-muted-foreground py-2">
                            {permissionsLoading ? 'Loading features...' : 'No features available'}
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Features - Mobile List / Desktop Table (Current User's Features) */}
            {features && (
              <div className="w-full max-w-full">
                <h3 className="text-lg font-semibold mb-3">Your Current Features</h3>
                
                {/* Mobile View - List Format */}
                <div className="block sm:hidden space-y-3">
                  {featuresToDisplay.length > 0 ? (
                    featuresToDisplay.map((featureMeta) => {
                      // Special handling for audio_limits
                      if (featureMeta.name === 'audio_limits') {
                        const audioLimits = TIER_AUDIO_LIMITS[tier] || TIER_AUDIO_LIMITS.free;
                        return (
                          <div key={featureMeta.name} className="flex items-center justify-between p-3 border rounded-lg">
                            <span className="font-medium">{featureMeta.display_name}</span>
                            <div className="text-sm text-right">
                              <Badge variant="outline">{audioLimits.maxFileSizeMb}MB</Badge>
                              <div className="text-xs text-muted-foreground mt-1">
                                {audioLimits.displayFormats}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Special handling for number_of_accounts
                      if (featureMeta.name === 'number_of_accounts') {
                        const accountCount = tier === 'family' ? 4 : 1;
                        return (
                          <div key={featureMeta.name} className="flex items-center justify-between p-3 border rounded-lg">
                            <span className="font-medium">{featureMeta.display_name}</span>
                            <Badge variant="outline" className="text-sm">
                              {accountCount}
                            </Badge>
                          </div>
                        );
                      }

                      const hasFeature = features[featureMeta.name as keyof typeof features] === true;
                      return (
                        <div key={featureMeta.name} className="flex items-center justify-between p-3 border rounded-lg">
                          <span className="font-medium">{featureMeta.display_name}</span>
                          {hasFeature ? (
                            <Badge variant="default" className="bg-green-500">
                              Available
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              Not Available
                            </Badge>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-3 border rounded-lg text-center text-muted-foreground">
                      {permissionsLoading ? 'Loading features...' : 'No features available'}
                    </div>
                  )}
                  {/* Upload Limit is not a permission, keep it separate */}
                  {features.daily_upload_limit !== undefined && (
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="font-medium">Upload Limit</span>
                      {features.daily_upload_limit !== null ? (
                        <Badge variant="outline">
                          {features.daily_upload_limit}
                        </Badge>
                      ) : (
                        <Badge variant="default" className="bg-purple-500">
                          Unlimited
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                {/* Desktop View - Table Format */}
                <div className="hidden sm:block border rounded-lg overflow-x-auto w-full">
                  <Table className="w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Feature</TableHead>
                        <TableHead className="text-right">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {featuresToDisplay.map((featureMeta) => {
                        // Special handling for audio_limits
                        if (featureMeta.name === 'audio_limits') {
                          const audioLimits = TIER_AUDIO_LIMITS[tier] || TIER_AUDIO_LIMITS.free;
                          return (
                            <TableRow key={featureMeta.name}>
                              <TableCell>
                                {renderFeatureName(featureMeta)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Badge variant="outline">{audioLimits.maxFileSizeMb}MB</Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {audioLimits.displayFormats}
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        // Special handling for number_of_accounts
                        if (featureMeta.name === 'number_of_accounts') {
                          const accountCount = tier === 'family' ? 4 : 1;
                          return (
                            <TableRow key={featureMeta.name}>
                              <TableCell>
                                {renderFeatureName(featureMeta)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline" className="text-sm">
                                  {accountCount}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        const hasFeature = features[featureMeta.name as keyof typeof features] === true;
                        return (
                          <TableRow key={featureMeta.name}>
                            <TableCell>
                              {renderFeatureName(featureMeta)}
                            </TableCell>
                            <TableCell className="text-right">
                              {hasFeature ? (
                                <Badge variant="default" className="bg-green-500">
                                  Available
                                </Badge>
                              ) : (
                                <Badge variant="destructive">
                                  Not Available
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Upload Limit is not a permission, keep it separate */}
                      {features.daily_upload_limit !== undefined && (
                        <TableRow>
                          <TableCell className="font-medium">Upload Limit</TableCell>
                          <TableCell className="text-right">
                            {features.daily_upload_limit !== null ? (
                              <Badge variant="outline">
                                {features.daily_upload_limit}
                              </Badge>
                            ) : (
                              <Badge variant="default" className="bg-purple-500">
                                Unlimited
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Cancel Subscription Section */}
            {subscriptionStatus && 
             subscriptionStatus.subscription_tier !== 'free' && 
             subscriptionStatus.subscription_status === 'active' &&
             !subscriptionStatus.is_complimentary && (
              <div className="w-full max-w-full mt-6">
                <Separator className="mb-4" />
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">Cancel Subscription</h3>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setShowCancelDialog(true)}
                    className="shrink-0"
                    disabled={subscriptionStatus.downgrade_pending && subscriptionStatus.pending_tier === 'free'}
                  >
                    {subscriptionStatus.downgrade_pending && subscriptionStatus.pending_tier === 'free' 
                      ? 'Cancellation Pending' 
                      : 'Cancel Subscription'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? Your subscription will remain active until{' '}
              {subscriptionStatus?.next_billing_date 
                ? new Date(subscriptionStatus.next_billing_date).toLocaleDateString()
                : 'the end of your billing period'}.
              After cancellation, you will lose access to premium features.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={cancelling}
            >
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  setCancelling(true);
                  const result = await paymentService.cancelSubscription();
                  
                  // Check if cancellation requires approval (redirect to PayPal)
                  if (result?.requires_approval && result?.approval_url) {
                    // Redirect to PayPal approval page
                    window.location.href = result.approval_url;
                    return;
                  }
                  
                  // Direct cancellation successful
                  toastHelper.newMessage(
                    'success',
                    'Subscription Cancelled',
                    'Your subscription has been cancelled successfully. You will retain access until the end of your billing period.'
                  );
                  setShowCancelDialog(false);
                  // Refresh subscription status
                  window.location.reload();
                } catch (error: any) {
                  toastHelper.newMessage(
                    'error',
                    'Cancellation Failed',
                    error?.response?.data?.message || 
                    'Failed to cancel subscription. Please try again.'
                  );
                } finally {
                  setCancelling(false);
                }
              }}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SubscriptionLimits;

